import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { FileText, Save, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContent = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [editedContent, setEditedContent] = useState({});

  const sectionInfo = {
    hero: { label: 'Hero Section', description: 'Main banner at the top of the page' },
    mission: { label: 'Our Mission', description: 'Mission statement banner' },
    music: { label: 'Listen Now', description: 'Music streaming section' },
    story: { label: 'Our Story', description: 'Founder story section' },
    process: { label: 'Our Process', description: '3-step process section' },
    cta: { label: 'Call to Action', description: 'Shop button and links' }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/content`);
      const data = await response.json();
      setSections(data);
      
      // Initialize edited content
      const initial = {};
      data.forEach(section => {
        initial[section.section_name] = { ...section };
      });
      setEditedContent(initial);
    } catch (err) {
      console.error('Error fetching content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (sectionName, field, value) => {
    setEditedContent(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        [field]: value
      }
    }));
  };

  const saveSection = async (sectionName) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/content/${sectionName}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editedContent[sectionName])
      });

      if (response.ok) {
        alert(`${sectionInfo[sectionName]?.label || sectionName} saved successfully!`);
        await fetchContent();
      }
    } catch (err) {
      console.error('Error saving content:', err);
      alert('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (sectionName, field, label, type = 'text') => {
    const value = editedContent[sectionName]?.[field] || '';
    
    if (type === 'textarea') {
      return (
        <div>
          <label className="block text-white mb-2 text-sm">{label}</label>
          <textarea
            value={value}
            onChange={(e) => handleChange(sectionName, field, e.target.value)}
            rows="4"
            className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none resize-none"
          />
        </div>
      );
    }

    return (
      <div>
        <label className="block text-white mb-2 text-sm">{label}</label>
        <input
          type={type}
          value={value}
          onChange={(e) => handleChange(sectionName, field, e.target.value)}
          className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
        />
      </div>
    );
  };

  const renderSectionFields = (section) => {
    const name = section.section_name;

    switch (name) {
      case 'hero':
        return (
          <div className="space-y-4">
            {renderField(name, 'title', 'Title')}
            {renderField(name, 'subtitle', 'Subtitle')}
            {renderField(name, 'description', 'Tagline')}
            {renderField(name, 'video_url', 'Video URL')}
            {renderField(name, 'image_url', 'Fallback Image URL')}
          </div>
        );
      
      case 'mission':
        return (
          <div className="space-y-4">
            {renderField(name, 'title', 'Title')}
            {renderField(name, 'description', 'Mission Text', 'textarea')}
            {renderField(name, 'image_url', 'Background Image URL')}
          </div>
        );
      
      case 'music':
        return (
          <div className="space-y-4">
            {renderField(name, 'title', 'Title')}
            {renderField(name, 'subtitle', 'Subtitle')}
            {renderField(name, 'description', 'Description', 'textarea')}
            {renderField(name, 'image_url', 'Background Image URL')}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2 text-sm">Apple Music URL</label>
                <input
                  type="text"
                  value={editedContent[name]?.metadata?.apple_music_url || ''}
                  onChange={(e) => handleChange(name, 'metadata', { ...editedContent[name]?.metadata, apple_music_url: e.target.value })}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-white mb-2 text-sm">Spotify URL</label>
                <input
                  type="text"
                  value={editedContent[name]?.metadata?.spotify_url || ''}
                  onChange={(e) => handleChange(name, 'metadata', { ...editedContent[name]?.metadata, spotify_url: e.target.value })}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                />
              </div>
            </div>
          </div>
        );
      
      case 'story':
        return (
          <div className="space-y-4">
            {renderField(name, 'title', 'Section Title')}
            {renderField(name, 'subtitle', 'Founder Name')}
            {renderField(name, 'description', 'Story Text', 'textarea')}
            {renderField(name, 'image_url', 'Founder Photo URL')}
          </div>
        );
      
      case 'process':
        return (
          <div className="space-y-4">
            {renderField(name, 'title', 'Section Title')}
            <p className="text-gray-400 text-sm">
              Process steps are managed through the metadata. Contact support to update the 3-step process content.
            </p>
          </div>
        );
      
      case 'cta':
        return (
          <div className="space-y-4">
            {renderField(name, 'button_text', 'Button Text')}
            {renderField(name, 'button_link', 'Shop URL')}
          </div>
        );
      
      default:
        return (
          <div className="space-y-4">
            {renderField(name, 'title', 'Title')}
            {renderField(name, 'subtitle', 'Subtitle')}
            {renderField(name, 'description', 'Description', 'textarea')}
            {renderField(name, 'image_url', 'Image URL')}
          </div>
        );
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Content Management</h1>
          <p className="text-gray-400">Edit page sections and content</p>
        </div>

        {/* Content Sections */}
        {loading ? (
          <div className="text-center text-gray-400 py-12">Loading content...</div>
        ) : sections.length === 0 ? (
          <div className="text-center text-gray-400 py-12">
            <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>No content sections found.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sections.map((section) => (
              <Card key={section.section_name} className="bg-zinc-900 border-zinc-800">
                <CardHeader
                  className="cursor-pointer"
                  onClick={() => setExpandedSection(
                    expandedSection === section.section_name ? null : section.section_name
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white">
                        {sectionInfo[section.section_name]?.label || section.section_name}
                      </CardTitle>
                      <p className="text-gray-400 text-sm mt-1">
                        {sectionInfo[section.section_name]?.description || 'Page section'}
                      </p>
                    </div>
                    {expandedSection === section.section_name ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </CardHeader>
                
                {expandedSection === section.section_name && (
                  <CardContent className="border-t border-zinc-800 pt-4">
                    {renderSectionFields(section)}
                    
                    <Button
                      onClick={() => saveSection(section.section_name)}
                      disabled={saving}
                      className="bg-orange-500 hover:bg-orange-600 text-black mt-6 w-full"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminContent;
